import { Box, Button, Flex, FormControl, FormHelperText, FormLabel, Stack, Switch, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, useToast } from '@chakra-ui/react';

import {
    createColumnHelper,
    CellContext,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import { useMemo, useState } from 'react';
import { OrganizationMemberDto } from '../../../models/organization/dto/organization.dto';
import { translate } from '../../../i18n';
import { dateToString } from '../../../utils/date';
import { useGetSelectedOrganizationView } from '../hooks/useGetCurrentOrganization';
import { useSelectedOrganization } from '../hooks/useSelectedOrganization';
import { useAddMemberToOrganization } from '../hooks/useAddMemberToOrganization';
import { useElevatedAdminAccess } from '../hooks/useIsElevatedAdmin';
import { dashboardSignUpUrl } from '../utils/dashboard-sign-up-url';
import ErrorState from '../../../components/states/ErrorState';


const columnHelper = createColumnHelper<OrganizationMemberDto>();

export default function OrganizationMemberList() {
    const selectedOrganization = useSelectedOrganization.use.selectedOrganization();
    const { data, isError } = useGetSelectedOrganizationView(selectedOrganization?.id);

    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();
    const columns = useMemo(() => [
        columnHelper.accessor('email', {
            id: 'email',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color='gray.400'>
                    {translate('organization.email')}
                </Text>
            ),
            cell: (info: CellContext<OrganizationMemberDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color='gray.400'>
                    {translate('organization.joined_at')}
                </Text>
            ),
            cell: (info: CellContext<OrganizationMemberDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {dateToString(new Date(info.getValue()))}
                    </Text>
                </Flex>
            )
        })
    ], [textColor]);
    const table = useReactTable({
        data: data?.organization.members || [],
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inviteeEmail, setInviteeEmail] = useState('');
    // Platform-admin "force add" option (the API refuses it for anyone else):
    // add silently, without the "you've been added" email. No dashboard account
    // is created for them either way — they sign up with this email and the
    // club is matched by email.
    const { isElevated: isElevatedAdmin } = useElevatedAdminAccess();
    const [notify, setNotify] = useState(true);

    const openModal = () => {
        setIsModalOpen(true);
    };
    const closeModal = () => {
        setIsModalOpen(false);
        setInviteeEmail('');
        setNotify(true);
    };

    const { mutateAsync: addMemberToOrganization, isLoading: isAdding } = useAddMemberToOrganization(selectedOrganization?.id, () => {
        closeModal();
    });
    const handleSubmit = () => {
        if (selectedOrganization?.id) {
            addMemberToOrganization({
                email: inviteeEmail,
                organizationId: selectedOrganization.id,
                ...(isElevatedAdmin ? { notify } : {}),
            }).then(() => {
                toast({ title: translate('organization.add_member_success'), status: 'success', duration: 3000 });
            }).catch((error: Error) => {
                toast({ title: translate('common.error'), description: error?.message, status: 'error', duration: 5000 });
            });
        }
    };

    if (isError) {
        return (
            <PageCard title={translate('organization.members')}>
                <ErrorState message={translate('common.error')} />
            </PageCard>
        );
    }

    return (
        <PageCard
            title={translate('organization.members')}
            rightHeader={
                <>
                    <Button colorScheme="brand" onClick={openModal}>
                        {translate('organization.add_member')}
                    </Button>
                    <FormModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        title={translate('organization.add_member_title')}
                        onSubmit={handleSubmit}
                        isSubmitting={isAdding}
                        submitLabel={translate('common.submit')}
                        cancelLabel={translate('common.cancel')}
                    >
                        <Text fontSize="sm" color="gray.500" mb={4}>
                            {translate('organization.add_member_description')}
                        </Text>
                        <FormField
                            type="email"
                            label={translate('organization.email')}
                            value={inviteeEmail}
                            onChange={setInviteeEmail}
                            placeholder="example@domain.com"
                        />
                        {isElevatedAdmin === true && (
                            <Stack mt={4} spacing={3}>
                                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color="gray.500">
                                    {translate('organization.force_add_section')}
                                </Text>
                                <FormControl>
                                    <Flex align="center" justify="space-between">
                                        <FormLabel mb={0} fontSize="sm">{translate('organization.force_add_notify')}</FormLabel>
                                        <Switch isChecked={notify} onChange={(e) => setNotify(e.target.checked)} />
                                    </Flex>
                                    {!notify && (
                                        <FormHelperText>
                                            {translate('organization.force_add_silent_help', { url: dashboardSignUpUrl() })}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Stack>
                        )}
                    </FormModal>
                </>
            }
        >
            <Box>
                <Table variant='simple' color='gray.500' mb={6} mt={3}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            pe='10px'
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                justifyContent='space-between'
                                                align='center'
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color='gray.400'>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                fontSize={{ sm: '14px' }}
                                                minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                borderColor='transparent'>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </Box>
        </PageCard>
    );
}
